import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { RewardedGateProvider } from '../contexts/RewardedGateContext'
import Header from './Header'
import BottomNavigation from './BottomNavigation'
import BackToTopButton from './BackToTopButton'
import AdSlot from './AdSlot'

/**
 * Layout wrapper for authenticated routes.
 * Renders Header and navigation for logged-in users.
 * The marketing Footer is intentionally omitted from the app shell -
 * it appears only on the public Landing page and other marketing pages.
 * Redirects to login if not authenticated.
 *
 * Also acts as the onboarding gate: if the user hasn't completed the
 * guided wizard, redirects them to /onboarding (except on safe escape
 * routes like /settings, /privacy, /support).
 */
const ProtectedLayout = ({ groups, onJoinSuccess }) => {
  const { user, onboardingCompleted, onboardingLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  if (!user) {
    return <Navigate to="/login" replace />
  }

  const isOnboardingRoute = location.pathname.startsWith('/onboarding')
  const onboardingEscapeRoutes = ['/settings', '/privacy', '/support']
  const isEscapeRoute = onboardingEscapeRoutes.some((p) => location.pathname.startsWith(p))

  // Gate: send users with no onboarding record to /onboarding. We wait for the
  // initial fetch to settle so we don't briefly bounce existing users.
  if (!onboardingLoading && !onboardingCompleted && !isOnboardingRoute && !isEscapeRoute) {
    return <Navigate to="/onboarding" replace />
  }

  // On the onboarding route, suppress the regular app shell (Header + bottom nav)
  // so the wizard owns the viewport.
  if (isOnboardingRoute) {
    return (
      <main>
        <Outlet context={{ groups, onJoinSuccess }} />
      </main>
    )
  }

  return (
    <RewardedGateProvider>
      <Header
        onJoinSuccess={onJoinSuccess}
        onSettingsClick={() => navigate('/settings')}
      />
      <main>
        <Outlet context={{ groups, onJoinSuccess }} />
      </main>
      {/* Desktop side-rail ads (free web users, very wide viewports only).
          AdSlot renders null below its breakpoint, for Pro, and on iOS. */}
      <AdSlot variant="siderail" className="fixed left-3 top-24 z-30 w-[160px]" />
      <AdSlot variant="siderail" className="fixed right-3 top-24 z-30 w-[160px]" />
      <BottomNavigation />
      <BackToTopButton />
    </RewardedGateProvider>
  )
}

export default ProtectedLayout
